import { Link } from "react-router-dom";
import { ArrowRight, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageShell } from "@/components/layout/PageShell";
import { ConnectionDiagram } from "@/components/landing/ConnectionDiagram";
import { HeroSteps } from "@/components/landing/HeroSteps";
import { PoweredByPybricks } from "@/components/layout/PoweredByPybricks";

// "Built around one thing" live-link section: hidden until further notice. Set to true to bring it back.
const SHOW_LIVE_LINK = false;
// "Your class could be driving by lunchtime" panel: hidden until further notice.
const SHOW_CLASS_CTA = false;

export default function Landing() {
  return (
    <PageShell testId="landing-page">
      <section className="relative overflow-hidden border-b">
        <div className="grid-bg absolute inset-0 opacity-70" />
        <div className="relative mx-auto max-w-7xl px-4 py-14 sm:px-8 lg:py-20">
          <div className="grid items-end gap-8 lg:grid-cols-[1.1fr_0.9fr] lg:gap-14">
            <div>
              <p className="eyebrow fade-up inline-flex items-center gap-2 rounded-full border bg-card px-3 py-1.5 text-accent dark:text-primary">
                <span className="h-1.5 w-1.5 rounded-full bg-accent dark:bg-primary" />Free for every student and classroom
              </p>
              <h1 className="fade-up delay-1 mt-6 text-4xl font-extrabold leading-[1.02] tracking-tight sm:text-5xl lg:text-6xl">
                Build With SPIKE.<br />Drive with an <span className="text-primary">Xbox controller.</span>
              </h1>
            </div>
            <div>
              <p className="fade-up delay-2 text-base text-muted-foreground md:text-lg">
                HubPilot.au is the easiest way to get a LEGO® Education SPIKE™ Prime robot driving with an Xbox controller: tell it how your robot is built, press Drive, and your hub pairs straight to the controller. Your robot runs on the free, official <a href="https://pybricks.com" target="_blank" rel="noreferrer" className="font-semibold text-foreground underline decoration-primary/50 underline-offset-4 hover:text-primary" data-testid="hero-pybricks-link">Pybricks</a> firmware.
              </p>
              <div className="fade-up delay-3 mt-5 flex flex-wrap gap-x-6 gap-y-2 text-sm text-muted-foreground">
                <span>✓ Free forever</span><span>✓ Chrome & Edge</span><span>✓ SPIKE Prime + Robot Inventor</span>
              </div>
              <div className="fade-up delay-3 mt-7 flex flex-wrap gap-3">
                <Button asChild size="lg" className="rounded-full bg-accent px-7 text-accent-foreground hover:bg-accent/90">
                  <Link to="/try" data-testid="hero-start-button">Set up a robot free <ArrowRight className="ml-1.5 h-4 w-4" /></Link>
                </Button>
                <Button asChild size="lg" variant="outline" className="rounded-full px-7">
                  <Link to="/how-it-works" data-testid="hero-how-button">How the connection works</Link>
                </Button>
              </div>
              <PoweredByPybricks className="fade-up delay-4 mt-6" testId="hero-powered-by-pybricks" />
            </div>
          </div>
          <HeroSteps />
        </div>
      </section>

      {SHOW_LIVE_LINK && (
        <section className="mx-auto max-w-7xl px-4 py-16 sm:px-8 lg:py-24" data-testid="live-link-section">
          <div className="grid items-center gap-10 lg:grid-cols-[0.85fr_1.15fr] lg:gap-16">
            <div>
              <p className="eyebrow text-accent dark:text-primary">Built around one thing</p>
              <h2 className="mt-3 text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl">A rock-solid link between hub and controller.</h2>
            </div>
            <ConnectionDiagram />
          </div>
        </section>
      )}

      {SHOW_CLASS_CTA && (
        <section className={`mx-auto max-w-7xl px-4 pb-16 sm:px-8 lg:pb-24 ${SHOW_LIVE_LINK ? "" : "pt-16 lg:pt-24"}`} data-testid="class-cta-section">
          <div className="relative overflow-hidden rounded-[2rem] bg-primary p-8 text-primary-foreground sm:p-12 lg:p-16">
            <div className="absolute -right-16 -top-16 h-64 w-64 rounded-full bg-accent/90 dark:bg-white/10" />
            <div className="relative max-w-2xl">
              <Zap className="h-8 w-8" />
              <h2 className="mt-4 text-3xl font-extrabold tracking-tight sm:text-4xl lg:text-5xl">Your class could be driving by lunchtime.</h2>
              <p className="mt-4 text-base opacity-90 md:text-lg">Make a free account, flash the hubs once, and every student can set up and drive their own robot.</p>
              <Button asChild size="lg" className="mt-8 rounded-full bg-white px-7 text-slate-900 hover:bg-white/90">
                <Link to="/sign-up" data-testid="cta-sign-up-button">Create a free account</Link>
              </Button>
            </div>
          </div>
        </section>
      )}
    </PageShell>
  );
}
